export const PROJECTS = [
//nukepc-website
{
  id: "nukepc-website",
  title: "NukePC Website",
  description: "Redesign to improve clarity, engagement, and conversion.",
  image: "/assets/card-img-nw.png",
  lockedPreview: "/assets/blur-ss-1.png",
  locked: true,
  tags: ["Freelancing", "Protected"],
  techstack: ["Figma"],
  type: "featured",
  link: "/project/nukepc-website",
  accent: "#FF5722",
  status: "protected",
  hero: "/assets/nuke-web-bg.png",
  intro:
    "A targeted redesign of key website sections to enhance clarity, engagement, and conversion while staying aligned with existing brand guidelines.",
  meta: {
    role: "UI/UX Designer",
    team: "Freelance",
    timeline: "2026",
    skills: ["Landing page design", "Conversion UX", "Figma"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Protected",
      status: "done",
      title: "A sharper website for custom PC buyers",
      body:
        "NukePC is a custom PC building company in Chennai and during my internship, I redesigned select sections of their website based on their existing design system. The focus was on improving visual hierarchy, refining layout structure, and making the experience more intuitive without deviating from brand constraints. I worked on high-impact areas like the hero section and the 'How It Works' flow, ensuring clearer communication of value and process. The redesign introduces stronger typography contrast, better spacing, and more guided user flow — making the experience feel more premium, structured, and conversion-focused while still staying true to the original design language.",
      layout: "text",
    },
    {
      id: "my-role",
      eyebrow: "My Role",
      sidebarTitle: "My Role",
      sidebarSubtitle: "Designer",
      status: "done",
      title: "What I focused on",
      body:
        "The work centered on improving the website experience without replacing the existing design language.",
      layout: "options",
      options: [
        {
          title: "Visual Design",
          body:
            "Created a cohesive dark-mode design system with a black and orange brand palette, consistent typography using bold Fractul-style headers, and custom component patterns across all screens.",
          image: "/assets/nuke-web-card.png",
        }
      ],
    },
    {
      id: "hero-section",
      eyebrow: "Screens & Flows",
      sidebarTitle: "Hero Section",
      sidebarSubtitle: "Landing section redesign",
      status: "done",
      title: "Hero Section",
      body:
        "Redesigned the landing hero to create a strong first impression with a clear value proposition and primary call-to-action. Improved visual hierarchy using bold typography, contrast, and spacing, combined with a human + product visual to increase trust and relatability.",
      layout: "slideshow",
      images: ["/assets/nuke-web-card.png", "/assets/nuke-web-1.png"],
    },
    {
      id: "how-it-works",
      eyebrow: "How It Works",
      sidebarTitle: "How It Works",
      sidebarSubtitle: "Process redesign",
      status: "done",
      title: "How It Works",
      body:
        "Reworked the process section into a structured, step-by-step flow combining visual elements and textual explanation. Introduced progressive disclosure to break complex information into digestible steps, reducing cognitive load and improving clarity.",
      layout: "slideshow",
      images: [
        "/assets/nuke-web-2.png",
        "/assets/nuke-web-3.png",
        "/assets/nuke-web-4.png",
      ],
    },
    {
      id: "key-decisions",
      eyebrow: "Key Decisions & Rationale",
      sidebarTitle: "Key Decisions",
      sidebarSubtitle: "Why these changes were made",
      status: "done",
      title: "Key decisions and rationale",
      layout: "rationale-list",
      items: [
        {
          label: "01 — Refinement\nover reinvention",
          title: "Designing within constraints",
          body:
            "The goal was not to redesign everything, but to improve clarity and structure within the existing system — a more realistic product design approach.",
        },
        {
          label: "02 — Visual\nhierarchy",
          title: "Stronger visual hierarchy",
          body:
            "Typography scale, spacing, and contrast were refined to guide user attention toward key actions and information.",
        },
        {
          label: "03 — Progressive\ndisclosure",
          title: "Structured information flow",
          body:
            "Breaking information into smaller, digestible parts reduces cognitive load and helps users understand complex processes more easily.",
        },
        {
          label: "04 — Dark-first\nconsistency",
          title: "Refined dark design system",
          body:
            "Maintained the existing dark theme while improving readability and using accent colors more intentionally to highlight actions.",
        },
        {
          label: "05 — Conversion-\nfocused layout",
          title: "Guided user flow",
          body:
            "Each section was structured to answer what it is, why it matters, and what to do next — improving conversion potential.",
        },
      ],
    },{
  id: "delivered-and-learned",
  eyebrow: "Outcome",
  sidebarTitle: "Outcome",
  sidebarSubtitle: "Delivered and learned",
  status: "done",
  title: "What was delivered and what I learned",
  layout: "split-insights",
  columns: [
    {
      title: "What was delivered",
      items: [
        {
          label: "Redesigned key website sections",
          body:
            "Hero section and process flow redesigned to improve clarity, structure, and usability.",
        },
        {
          label: "Improved visual hierarchy",
          body:
            "Better typography, spacing, and contrast for enhanced readability and focus.",
        },
        {
          label: "Structured content flow",
          body:
            "Information reorganized to guide users more clearly through the experience.",
        },
        {
          label: "Design aligned with existing system",
          body:
            "Enhancements made without breaking brand consistency or existing design language.",
        },
      ],
    },
    {
      title: "What I learned",
      items: [
        {
          tag: "Working with constraints",
          body:
            "Learned how to improve an existing product without redesigning everything — focusing on clarity over creativity.",
        },
        {
          tag: "Hierarchy & structure",
          body:
            "Small changes in spacing, typography, and layout can significantly improve usability and comprehension.",
        },
        {
          tag: "Conversion thinking",
          body:
            "Designing sections to guide users toward action, not just visual appeal, is key in real-world products.",
        },
      ],
    },
  ],
}
  ],
},
//nukepc-app
{
  id: "nukepc-mobile",
  title: "NukePC Mobile App",
  description:
    "A mobile-first platform for designing and configuring custom PCs.",
  image: "/assets/card-img-na.png",
  lockedPreview: "/assets/blur-ss-2.png",
  locked: true,
  tags: ["Internship", "Protected"],
  techstack: ["Figma"],
  type: "featured",
  link: "/project/nukepc-mobile",
  accent: "#FF5722",
  status: "protected",
  hero: "/assets/nuke-ss-bg.png",
  intro:
    "A protected internship project focused on a mobile-first custom PC configuration experience, designed to reduce friction and help both beginners and enthusiasts build with confidence.",
  meta: {
    role: "UI/UX Design Intern",
    team: "NukePC",
    timeline: "Dec 2025 - Jan 2026",
    skills: ["Figma", "Mobile UI", "Product flows"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Protected",
      status: "done",
      title: "A mobile-first PC configuration experience",
      body:
        "This project focused on transforming the PC building journey into a guided mobile product that felt easier to understand, more trustworthy, and more enjoyable to complete.",
      layout: "text",
    },

    {
      id: "before-we-begin",
      eyebrow: "Before We Begin",
      sidebarTitle: "Before We Begin",
      sidebarSubtitle: "Project framing",
      status: "done",
      title: "Before we begin",
      body:
        "The project started by defining what the app should do, who it should serve, and what I was responsible for shaping.",
      layout: "options",
      options: [
        {
          title: "What was the app for?",
          body:
            "The app was designed for customers to submit their requirements and budget, based on which the company would configure a PC build. Once the user accepted the provided configuration, the PC would be built and delivered.",
          image: "/assets/nuke-ss-1.png",
        },
        {
          title: "Who is it for?",
          body:
            "Customers looking to build a PC, along with companies and startups that need multiple custom systems for work.",
          image: "/assets/nuke-ss-1.png",
        },
        {
          title: "What was my role?",
          body:
            "I planned the overall app flow, made sure the design stayed within implementation guidelines, and designed screens while keeping cognitive load, friction, and handoff practicality in mind.",
          image: "/assets/nuke-ss-1.png",
        },
      ],
    },

    {
      id: "existing-system",
      eyebrow: "Existing System",
      sidebarTitle: "Existing System",
      sidebarSubtitle: "What needed fixing",
      status: "done",
      title: "The reality of the existing system",
      layout: "rationale-list",
      items: [
        {
          label: "01 — Friction",
          title: "Too many steps",
          body:
            "The flow had too many steps, increasing friction and reducing retention.",
        },
        {
          label: "02 — Visual load",
          title: "The design felt stale",
          body:
            "The earlier design felt stale and visually heavy, forcing users to manually input most requirements.",
        },
        {
          label: "03 — Clarity",
          title: "Options were vague",
          body:
            "Some choices were too vague, making it harder for users with limited domain knowledge to proceed confidently.",
        },
      ],
    },

    {
      id: "decision-shapers",
      eyebrow: "Decision Shapers",
      sidebarTitle: "Decision Shapers",
      sidebarSubtitle: "Constraints",
      status: "done",
      title: "What shaped the decisions",
      layout: "split-insights",
      columns: [
        {
          title: "Time Constraints",
          items: [
            {
              tag: "Time Constraints",
              body:
                "I worked within a strict 45-day timeline for delivery. Being on-site, I collaborated closely with developers to ensure smooth implementation and adherence to design guidelines.",
            },
            {
              tag: "Execution Rhythm",
              body:
                "Each feature was completed in roughly a week, followed by review meetings where designs were explained, discussed, and iterated based on technical feedback.",
            },
          ],
        },
        {
          title: "Design Constraints",
          items: [
            {
              tag: "Design Constraints",
              body:
                "My initial instinct was to use more modern product-style typography to reinforce familiarity and trust, but a major constraint was staying aligned with existing brand guidelines.",
            },
            {
              tag: "System Consistency",
              body:
                "Instead of optimizing purely for UI preference, I aligned typography and spacing choices with the broader system, ensuring cohesion across the product.",
            },
          ],
        },
      ],
    },

    {
      id: "user-realities",
      eyebrow: "User Realities",
      sidebarTitle: "User Realities",
      sidebarSubtitle: "Behavioral insights",
      status: "done",
      title: "User realities",
      layout: "quote-points",
      quoteNote: "Simplify the overall process of the app but not to the point of abstraction.",
      items: [
        {
          quote:
            "The time it takes to make a decision increases with the number and complexity of choices.",
          body:
            "The initial PC requirements form behaved like a single-page experience, which created choice overload and increased friction for new users.",
        },
        {
          quote:
            "The average person can only keep 7 (plus or minus 2) items in their working memory.",
          body:
            "Multiple forms and inputs on the same page introduced decision fatigue, where users struggled to retain or accurately recall their earlier choices.",
        },
        {
          quote:
            "People judge an experience largely based on how they felt at its peak and at its end.",
          body:
            "Trust-building elements like showcased builds and testimonials were present, but not surfaced early enough in the experience where they mattered most.",
        },
      ],
    },

    {
      id: "design-goals",
      eyebrow: "Design Goals",
      sidebarTitle: "Design Goals",
      sidebarSubtitle: "What the redesign aimed for",
      status: "done",
      title: "Design goals",
      layout: "image-points",
      image: "/assets/nuke-ss-0001.png",
      points: [
        "Building on insights from the previous design, system flow, and user behavior, the experience was refined to reduce steps without over-abstracting the process.",
        "The interface was designed to feel professional while remaining grounded in implementation constraints and the delivery timeline.",
        "Subtle suggestions and contextual hints were introduced to support decision-making without overwhelming the user.",
        "Trust was established early by highlighting tangible achievements and testimonials from recognized individuals during onboarding.",
      ],
    },
  {
  id: "screens",
  eyebrow: "Screens",
  sidebarTitle: "Screens",
  sidebarSubtitle: "Key pages",
  status: "done",
  title: "Key screens in the experience",
  body:
    "The final interface direction focused on simplifying complex shopping and PC-building journeys into more structured, visually guided, and confidence-driven screens across the platform.",
  layout: "stacked-media-list",
  items: [
    {
      title: "Onboarding Experience",
      subtitle:
        "Introduced the platform through guided onboarding flows that explained the product ecosystem, showcased testimonials, and helped users quickly understand the value of building or buying PCs through the platform.",
      image:  "/assets/nuke-onb-1.gif",
    },
    {
      title: "Homepage Experience",
      subtitle:
        "The homepage was redesigned as a modern storefront inspired by adaptive ecommerce experiences, featuring dynamic hero sections, featured products, PC categories, accessories, and curated component collections for easier exploration.",
      image:  "/assets/nuke-home-1.gif",
    },
    {
      title: "Category and Product Discovery",
      subtitle:
        "Dedicated browsing screens helped users explore prebuilts and product collections through featured banners, structured product grids, and streamlined filter and sorting systems designed for faster comparison and decision-making.",
      image:  "/assets/nuke-cat-1.gif",
    },
    {
      title: "Product Details Page",
      subtitle:
        "Product pages focused on reducing purchase hesitation through immersive galleries, specification breakdowns, pricing visibility, compatibility information, and contextual recommendations alongside clear purchase actions.",
      image:  "/assets/nuke-prod-1.gif",
    },
    {
      title: "Build Now Flow",
      subtitle:
        "The custom PC-building experience replaced traditional technical forms with interactive sliders and guided inputs that captured user preferences like budget, usage intent, and performance expectations in a more approachable way.",
      image:  "/assets/nuke-build-1.gif",
    },
    {
      title: "Profile and Configuration Hub",
      subtitle:
        "User profile screens centralized saved configurations, carts, purchase history, and account management to create continuity between browsing, purchasing, and future upgrades within the ecosystem.",
      image:  "/assets/nuke-prof-1.gif",
    },
  ],
},
    {
      id: "key-decisions",
      eyebrow: "Key Decisions",
      sidebarTitle: "Key Decisions",
      sidebarSubtitle: "Why the app feels this way",
      status: "done",
      title: "Key decisions",
      layout: "rationale-list",
      items: [
        {
          label: "01 — Dark-first design",
          title: "Dark-first design system",
          body:
            "PC enthusiast culture is tied to dark environments. Black with orange accents felt native to the audience and reduced eye strain during long config sessions.",
        },
        {
          label: "02 — Custom inputs",
          title: "Custom wizard input controls",
          body:
            "Sliders, drum-pickers, and arc-dials made spec selection feel like an experience rather than a generic form.",
        },
        {
          label: "03 — Persistent FAB",
          title: "Persistent Build Now action",
          body:
            "The core action needed to stay reachable from anywhere, so a floating action button reduced friction across the flow.",
        },
        {
          label: "04 — Progressive disclosure",
          title: "Progressive disclosure in the wizard",
          body:
            "One question per screen with contextual explanations reduced cognitive load and improved completion for first-time builders.",
        },
        {
          label: "05 — Social proof",
          title: "Social proof in onboarding",
          body:
            "Real testimonials were surfaced early rather than buried later, helping establish trust before sign-up.",
        },
        {
          label: "06 — Domain-first entry",
          title: "Domain-first configuration entry",
          body:
            "Starting with what users need their PC for grounded the experience in their world rather than technical jargon.",
        },
      ],
    },

    {
      id: "what-was-delivered",
      eyebrow: "Delivery",
      sidebarTitle: "Delivered",
      sidebarSubtitle: "What shipped",
      status: "done",
      title: "What was delivered",
      layout: "text-list",
      items: [
        {
          title: "Complete app design delivered",
          body:
            "All major screens were designed and handed off, including onboarding, auth, home, prebuilds, accessories, build wizard, profile, and support flows.",
        },
        {
          title: "Fully shipped mobile app",
          body:
            "The designs were implemented and the NukePC app went live on Android and iOS, used by real customers across India.",
        },
        {
          title: "Novel UX patterns for spec selection",
          body:
            "Custom interaction components were designed for a category that rarely gets this level of mobile UX attention.",
        },
        {
          title: "Consistent design system",
          body:
            "A cohesive visual language was established across more than 20 screens through shared typography, color tokens, component patterns, and iconography.",
        },
      ],
    },

    {
      id: "what-i-learned",
      eyebrow: "Reflection",
      sidebarTitle: "Learned",
      sidebarSubtitle: "Takeaways",
      status: "done",
      title: "What I learned",
      layout: "text-list",
      tone: "dark",
      items: [
        {
          title: "End-to-end ownership",
          body:
            "This was my first full product design experience on a live app. I learned how to make design decisions under real business, development, and timeline constraints.",
        },
        {
          title: "Audience depth",
          body:
            "Working with a niche technical audience taught me how to balance simplicity for newcomers with enough depth for enthusiasts.",
        },
        {
          title: "E-commerce UX",
          body:
            "I grew my understanding of e-commerce UX patterns such as product discovery, filter systems, and conversion-focused states that translate well to other product spaces.",
        },
      ],
    },
  ],
},
//rmkcet-website-redesign
{
  id: "rmkcet-website",
  title: "RMKCET Website Redesign",
  description:
    "A redesign focused on making the college website clearer, easier to navigate, and more usable across devices.",
  image: "/assets/card-img-clg.png",
  tags: ["Redesign", "Shipped"],
  techstack: ["PHP","CSS", "JavaScript", "Figma", "GSAP" ],
  type: "featured",
  link: "/project/rmkcet-website",
  status: "shipped",
  accent: "#00292a",
  hero: "/assets/clg-ss-bg.png",
  intro:
    "A redesign project focused on improving the usability, structure, and mobile responsiveness of the RMKCET website for students, parents, and visitors.",
  meta: {
    role: "Web Designer & Developer",
    team: "Solo redesign",
    timeline: "2026",
    skills: ["Figma", "Web Design", "Responsive UI"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Overview",
      status: "done",
      title: "A cleaner website for students and visitors",
      subtitle: "Making college information easier to find, scan, and trust.",
      body:
        "The RMKCET website redesign focused on simplifying navigation, improving content hierarchy, and creating a more modern and mobile-friendly browsing experience. The goal was to help students, parents, and prospective applicants reach important information faster without feeling overwhelmed by cluttered layouts or dense structure.",
      layout: "text",
    },

    {
      id: "context",
      eyebrow: "Context",
      sidebarTitle: "Context",
      sidebarSubtitle: "Background",
      status: "done",
      title: "The website had information, but not enough clarity",
      subtitle: "Useful content existed, but the experience made it harder to access.",
      body:
        "The original college website contained all the core academic and institutional information users needed, but it lacked strong structure and visual hierarchy. Important sections such as admissions, departments, placements, and announcements were buried within dense layouts, making the experience feel outdated and difficult to navigate, especially on smaller screens.",
      layout: "text",
    },

    {
      id: "problems",
      eyebrow: "Problems",
      sidebarTitle: "Problems",
      sidebarSubtitle: "Key pain points",
      status: "done",
      title: "The biggest issues came from hierarchy, density, and mobile usability",
      body:
        "The redesign began by identifying the friction points that made the existing experience harder to use than it needed to be.",
      layout: "options",
      options: [
        {
          title: "Dense navigation",
          body:
            "Too many links and sections competed for attention, making it difficult for users to quickly understand where to go next.",
          image: "/assets/clg_img_2.png",
        },
        {
          title: "Weak visual hierarchy",
          body:
            "Important content like admissions, departments, and notices did not stand out clearly, which reduced scanability and slowed down task completion.",
          image: "/assets/clg_img_3.png",
        },
        {
          title: "Poor mobile experience",
          body:
            "The structure felt difficult to browse on phones, even though a large portion of students were likely to access the site on mobile devices.",
          image: "/assets/clg_img_1.png",
        },
      ],
    },
   {
      id: "constraints",
      eyebrow: "Constraints",
      sidebarTitle: "Constraints",
      sidebarSubtitle: "Boundaries",
      status: "done",
      title: "The redesign had to improve usability without losing important institutional content",
      subtitle: "This was not just about visual polish.",
      body:
        "The challenge was to modernize the experience while respecting the volume of existing academic content and the expectations of an institutional website. The redesign needed to improve clarity and responsiveness without oversimplifying critical information or removing structure that students and faculty rely on.",
      layout: "single-image",
      image: "/assets/clg-ss-8.png",
      video: "/assets/clg-web-intro.mp4",
    },
    {
      id: "insights",
      eyebrow: "Insights",
      sidebarTitle: "Insights",
      sidebarSubtitle: "What became clear",
      status: "done",
      title: "A few clear insights shaped the redesign direction",
      layout: "text-list",
      items: [
        {
          title: "Students scan before they read",
          body:
            "The interface needed stronger grouping, headings, and spacing so users could quickly locate what mattered before reading full sections.",
        },
        {
          title: "Navigation needed prioritization",
          body:
            "Not every page deserved equal visual weight. High-frequency tasks needed to be surfaced more clearly than secondary institutional pages.",
        },
        {
          title: "Mobile could not be an afterthought",
          body:
            "A responsive structure was essential because many users are likely to interact with the website on their phones first.",
        },
        {
          title: "Institutional websites still need warmth",
          body:
            "The site needed to feel trustworthy and organized without becoming visually cold or mechanically dense.",
        },
      ],
    },

    {
      id: "user-realities",
      eyebrow: "User Realities",
      sidebarTitle: "User Realities",
      sidebarSubtitle: "Behavior and needs",
      status: "done",
      title: "Users came with specific goals, not time to explore",
      layout: "quote-points",
      quoteNote:
        "Most visitors are not browsing for inspiration. They are trying to find something specific as quickly as possible.",
      items: [
        {
          quote:
            "Users often visit college websites with one immediate task in mind.",
          body:
            "Whether it is checking admissions, finding department information, or reading notices, people usually arrive with a clear destination rather than a desire to explore the entire site.",
        },
        {
          quote:
            "When pages feel crowded, trust drops even if the content is correct.",
          body:
            "An overload of links, weak grouping, and inconsistent spacing can make a website feel harder to trust, even when the institution itself is credible.",
        },
        {
          quote:
            "Mobile users need fast structure, not just compressed desktop content.",
          body:
            "Smaller screens demand clearer prioritization, bigger tap targets, and simpler content flow rather than a scaled-down version of the same clutter.",
        },
      ],
    },

    {
      id: "design-goals",
      eyebrow: "Design Goals",
      sidebarTitle: "Design Goals",
      sidebarSubtitle: "What the redesign aimed for",
      status: "done",
      title: "The redesign focused on clarity, speed, and structure",
      layout: "image-points",
      image: "/assets/clg-ss-10.png",
      points: [
        "Make important information easier to scan and access.",
        "Improve page hierarchy so users can understand structure at a glance.",
        "Create a more mobile-friendly browsing experience.",
        "Modernize the interface without making it feel disconnected from the institution.",
        "Build a repeatable layout system for departments, notices, and academic content.",
      ],
    },
    {
      id: "screens",
      eyebrow: "Screens",
      sidebarTitle: "Screens",
      sidebarSubtitle: "Key pages",
      status: "done",
      title: "Key screens in the redesign",
      body:
        "The final direction focused on creating clearer, more structured screens for the most important user journeys across the website.",
      layout: "stacked-media-list",
      items: [
        {
          title: "Homepage Redesign",
          subtitle:
            "A cleaner landing experience with clearer navigation, stronger hierarchy, and better section rhythm.",
          images: [
            "/assets/clg-ss-10.png",
            "/assets/clg-ss-11.png",
          ],
        },
        {
          title: "Departments Overview",
          subtitle:
            "Department access was made more structured and discoverable so students could reach relevant academic pages faster.",
          images: [
            "/assets/clg-ss-6.png",
            "/assets/clg-ss-3.png",
          ],
        },
        {
          title: "Announcements and Notices",
          subtitle:
            "Notice layouts were simplified to make updates easier to spot without overwhelming the rest of the page.",
          images: [
            "/assets/clg-ss-8.png",
            "/assets/clg-ss-12.png",
            "/assets/clg-ss-13.png",
          ],
        },
      ],
    },
  ],
},
//case-study-ui
{
  id: "ceilang",
  title: "Ceilang",
  description:
    "Breaking the mental ceiling of language learning through conversational design.",
  image: "/assets/card-img-lng.png",
  tags: ["Concept", "Spec Design", "Mobile"],
  techstack: ["Figma"],
  type: "case-study",
  link: "/project/ceilang",
  status: "shipped",
  accent: "#8B6F5A",
  hero: "/assets/ceilang_img_hero.png",
  intro:
    "A concept mobile experience designed to make language learning feel more conversational, emotionally safe, and fluency-focused rather than lesson-focused.",
  meta: {
    role: "UI/UX Designer",
    team: "Solo Project",
    timeline: "02/05/2026 - 09/05/2026",
    skills: ["UI/UX Design", "Concept Design", "Mobile", "Figma"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Overview",
      status: "done",
      title: "A language learning concept built around staying in the conversation",
      subtitle:
        "Designed to help learners move past recognition and toward real conversational confidence.",
      body:
        "Ceilang is a concept mobile experience built around a simple but often ignored truth in language learning: recognition is not fluency. Many learners understand vocabulary, grammar, and structure, but still freeze when it is time to respond in real conversation. Ceilang was designed to address that ceiling by shifting the learning model away from streaks, exercises, and completion loops, and toward conversational rhythm, emotional safety, and adaptive immersion. Instead of treating language as a set of lessons to finish, the app treats it as something to stay inside long enough for it to start feeling natural.",
      layout: "text",
    },

    {
      id: "observation",
      eyebrow: "Observation",
      sidebarTitle: "Observation",
      sidebarSubtitle: "Where it started",
      status: "done",
      title: "The starting point was noticing that learning had already become conversational",
      subtitle:
        "Students were not just consuming information anymore. They were actively talking their way through confusion.",
      body:
        "The project began with an observation on a college bus. I kept seeing students on their phones, not scrolling casually or gaming, but actually chatting with AI about debugging code, understanding class concepts, and working through things they were stuck on. I was doing it too. The AI boom had quietly shifted how people were learning, especially engineering students, but most educational products still felt built for older learning behaviors. Around the same time, I kept coming back to language learning and the way so many people plateau. The more I looked at it, the more obvious the gap became: people were getting better at completing lessons, not at staying in conversations. That gap became the basis for Ceilang.",
      layout: "single-image",
      image: "/assets/ceilang_img_6.png",
    },

    {
      id: "problem",
      eyebrow: "The Problem",
      sidebarTitle: "The Problem",
      sidebarSubtitle: "The ceiling",
      status: "done",
      title: "The real barrier was not vocabulary, it was conversational paralysis",
      subtitle:
        "Most learners do not stop because they know nothing. They stop because they cannot stay inside real conversation long enough to feel fluent.",
      body:
        "There is a stage many language learners hit where they understand words, recognize sentence structures, and can follow media reasonably well, but still freeze the moment someone really speaks to them. This is the ceiling. It is not a knowledge problem as much as a rhythm and confidence problem. Existing platforms often optimize for streaks, levels, and accuracy, which measure consistency and completion more than communication. They help users recognize language passively, but they do not build comfort in using it actively, imperfectly, and in real time. Ceilang was designed around the belief that fluency is less about being error-free and more about staying in the conversation long enough for the language to start feeling natural.",
      layout: "single-image",
      image: "/assets/ceilang_img_7.png",
    },

    {
      id: "design-philosophy",
      eyebrow: "Design Philosophy",
      sidebarTitle: "Design Philosophy",
      sidebarSubtitle: "The system underneath",
      status: "done",
      title: "The product was designed to feel less like a classroom and more like a late-night chat",
      subtitle:
        "The core idea was conversational continuity: remove stopping points, reduce emotional friction, and make imperfect participation feel safe.",
      body:
        "Ceilang was built around the idea that language learning should feel like showing up to a conversation, not entering a lesson. That meant rethinking both product structure and AI behavior. Instead of acting like a strict tutor that corrects every mistake, the AI needed to feel like a fluent bilingual friend who understands intent, responds to meaning, and gently models better phrasing without stopping the flow. The same logic shaped the broader product. Traditional apps are designed around completion loops: finish, celebrate, exit. Ceilang needed the opposite loop, one that invites continuation. I also made a deliberate decision about levels. There are no levels as gates or unlock systems, but users can still see where they are in their language journey. The goal was to replace pressure with self-awareness and replace performance with comfort.",
      layout: "slideshow",
      images: [
        "/assets/ceilang_img_1.png",
        "/assets/ceilang_img_2.png",
        "/assets/ceilang_img_3.png",
        "/assets/ceilang_img_4.png",
      ],
    },

    {
      id: "core-experience",
      eyebrow: "Core Experience",
      sidebarTitle: "Core Experience",
      sidebarSubtitle: "How it works",
      status: "done",
      title: "Every part of the experience was designed to keep the learner talking",
      body:
        "The app centers continuity over correction, making each interaction feel like part of a living conversation rather than a test.",
      layout: "stacked-media-list",
      items: [
        {
          title: "Conversational Chat Interface",
          subtitle:
            "The main interaction is a natural chat interface where users can type imperfectly, mix languages, forget words, and still be understood. The AI responds to meaning first and weaves in better phrasing gently, without turning the moment into a correction exercise.",
          image: "/assets/ceilang_vid_2.gif",
        },
        {
          title: "Subtitle Support Layer",
          subtitle:
            "To reduce fear early on, Ceilang shows a soft subtitle translation beneath the user’s own message. This is not a literal dictionary-style translation, but a conversational equivalent closer to how a native speaker would actually say it. As confidence grows, that support fades naturally.",
          image: "/assets/ceilang_img_8.png",
        },
        {
          title: "Adaptive Immersion",
          subtitle:
            "The interface gradually leans more into the target language over time instead of forcing sudden full immersion. Early interactions provide more support, while later interactions trust the user more and step back only when comfort begins to build.",
          image: "/assets/ceilang_vid_3.gif",
        },
        {
          title: "Scenario-Based Roleplay",
          subtitle:
            "For learners who want more contextual structure, Ceilang offers believable conversation scenarios like ordering at a café, navigating a metro station, or talking at a music festival. These were designed to feel socially real rather than educationally staged.",
          image: "/assets/ceilang_img_9.png",
        },
        {
          title: "Passive Listening Mode",
          subtitle:
            "The passive listening experience was designed around exposure rather than testing. Instead of structured exercises, users listen to cinematic, natural-feeling conversations with optional captions, letting the language settle through rhythm and familiarity.",
          image: "/assets/ceilang_vid_1.gif",
        },
        {
          title: "Emotion-Led Notifications",
          subtitle:
            "Notification design was rethought completely. Instead of streak pressure or guilt-based reminders, Ceilang uses emotionally inviting language that feels like a moment pulling you back in rather than a task you forgot to complete.",
          image: "/assets/ceilang_img_10.png",
        },
      ],
    },

    {
      id: "visual-direction",
      eyebrow: "Visual Direction",
      sidebarTitle: "Visual Direction",
      sidebarSubtitle: "Mood and tone",
      status: "done",
      title: "The interface was designed to feel ambient, human, and emotionally calm",
      subtitle:
        "It needed to feel closer to a messaging app than a classroom.",
      body:
        "Visually, Ceilang moves away from anything that reads as educational in the traditional sense. There are no bright primary colors, mascots, or aggressive progress bars. Instead, the interface draws from messaging platforms and editorial layouts, using soft warm surfaces, restrained iconography, generous spacing, and typography that feels considered rather than instructional. The moodboard leaned toward ambient social interfaces, the kind of digital spaces people can sit inside for a long time without fatigue. The goal was to make conversations feel like conversations, not exercises disguised as chats.",
      layout: "text",
    },

    {
      id: "reflection",
      eyebrow: "Reflection",
      sidebarTitle: "Reflection",
      sidebarSubtitle: "What it taught me",
      status: "done",
      title: "The hardest part was not designing the interface, but designing for imperfection",
      subtitle:
        "The project forced me to think about how learning products can support people without interrupting them.",
      body:
        "Ceilang was a concept project, but the design questions behind it were deeply real. How do you build something that encourages imperfection instead of punishing it? How do you support a learner without constantly interrupting them? How do you make a product feel less like school and more like life? The biggest challenge was philosophical rather than visual. Most products guide users toward completion. Ceilang needed to guide users away from completion as the main goal and toward something harder to measure: comfort, rhythm, and confidence. It made me think more seriously about how emotional design, pacing, and interaction tone shape whether someone keeps going when they start to feel vulnerable.",
      layout: "text",
    },
  ],
},
//equora stock management-dashboard
{
  id: "equora",
  title: "Equora",
  description: "AI based Stock market analyzer",
  image: "/assets/card-img-eq.png",
  screen: "/assets/card-img-eq.png",
  tags: ["Fullstack", "Web", "AI"],
  techstack: [
    "React.js",
    "PostgreSQL",
    "Gemini API",
    "Recharts",
    "Tailwind CSS",
  ],
  type: "project",
  link: "/project/equora",
  status: "shipped",
  accent: "#4CAF50",
  hero: "/assets/eq-ss-1.png",
  intro:
    "An AI-based stock portfolio intelligence platform designed for users who find financial tools intimidating.",
  meta: {
    role: "Solo Project",
    team: "Solo",
    timeline: "11 Apr 2026 - 17 Apr 2026",
    skills: ["Fullstack", "UI Design", "Development", "Web"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Overview",
      status: "done",
      title: "A stock intelligence platform for people who feel locked out of finance",
      subtitle: "Built to make portfolios understandable before they feel technical.",
      body:
        "Equora is a stock portfolio intelligence platform designed and built for users who find financial tools intimidating. The platform lets users build and manage portfolios across 13,000+ real NASDAQ tickers, view asset allocation breakdowns, track historical performance across multiple time ranges, and receive AI-powered insights on trend direction, volatility, and drawdown in plain English. Built with React, Node.js, Express, and PostgreSQL, the backend handles around 26 million synthetic data points generated using Geometric Brownian Motion, the same stochastic model used in quantitative finance, at zero cost. The AI layer using Gemini API is scoped to component-level updates to stay fast and within free-tier limits. Every screen was designed around one question: what does someone see before they understand what they're looking at?",
      layout: "text",
    },

    {
      id: "my-role",
      eyebrow: "My Role",
      sidebarTitle: "My Role",
      sidebarSubtitle: "Design and build",
      status: "done",
      title: "I designed and built the product end to end",
      body:
        "This project covered frontend, backend, data architecture, and AI integration with the product experience designed around clarity for first-time users.",
      layout: "options",
      options: [
        {
          title: "Frontend Development",
          body:
            "Built the full React frontend with Vite, Tailwind CSS, and ShadCN, covering onboarding, portfolio management, historical charts via Recharts, and the AI insights panel.",
          image: "/assets/stock-15.png",
        },
        {
          title: "Backend & Data Architecture",
          body:
            "Designed and built the Node.js and Express API plus the PostgreSQL schema to handle around 26 million synthetic time-series rows. Wrote the Geometric Brownian Motion data generation pipeline applied across 13,000+ real NASDAQ tickers.",
          image: "/assets/stock-14.png",
        },
        {
          title: "AI Integration",
          body:
            "Integrated the Gemini API to analyze portfolio behavior and surface trend direction, volatility, and drawdown as plain-English insights, scoped to component-level triggers to stay within free-tier limits.",
          image: "/assets/stock-13.png",
        },
      ],
    },

{
  id: "flows",
  eyebrow: "Flows",
  sidebarTitle: "Flows",
  sidebarSubtitle: "Core product flows",
  status: "done",
  title: "The product was shaped around guided financial understanding",
  body:
    "Each flow was designed to reduce intimidation, explain itself quickly, and keep users oriented as they moved from setup to portfolio analysis and long-term tracking.",
  layout: "stacked-media-list",
  items: [
    {
      title: "User Onboarding & Portfolio Setup",
      subtitle:
        "The onboarding experience was designed as a gradual financial setup journey instead of a traditional registration form. Users first create their identity, enter basic profile details, and then import or manually add their existing stock holdings to establish a personalized starting point. The flow prioritizes clarity and reassurance, helping users feel in control before they ever reach analytics or market insights.",
      images: ["/assets/stock-1.png",
      "/assets/stock-2.png",
      "/assets/stock-3.png",
      ],
    },
    {
      title: "Portfolio Tracking Flow",
      subtitle:
        "The portfolio builder simplifies investment tracking through guided interactions that let users search by ticker or company name, add assets to a watchlist or portfolio, and view allocation updates in real time without manual calculations. The experience focuses on reducing friction while helping users understand portfolio composition progressively rather than all at once.",
      image: "/assets/stock-11.png",
    },
    {
      title: "Dashboard & Insights",
      subtitle:
        "The home dashboard surfaces the most critical signals first, including AI-generated portfolio health, top movers, asset allocation breakdowns, and market direction indicators. Progressive disclosure was used to prevent cognitive overload (mental strain caused by too much information at once), allowing users to absorb insights layer by layer instead of confronting dense financial data immediately.",
      images: ["/assets/stock-4.png",
      "/assets/stock-5.png",
      "/assets/stock-6.png",
      "/assets/stock-7.png",
      ],
    },
    {
      title: "AI Insights Engine",
      subtitle:
        "The AI layer analyzes portfolio behavior and communicates volatility, drawdown, and trend movement using plain-English interpretations instead of overly technical financial terminology. Component-level AI updates were intentionally scoped to keep the interface responsive while staying efficient within free-tier infrastructure limits.",
      images: ["/assets/stock-4.png",
      "/assets/stock-8.png",
      "/assets/stock-9.png",
      ],
    },
    {
      title: "Historical Performance & Smart Alerts",
      subtitle:
        "Historical tracking transforms raw market movement into a more readable narrative through selectable time ranges like 1W, 1M, 1Y, and 5Y. Contextual alerts notify users about concentration risk, unusual movement patterns, and downward pressure trends without creating notification fatigue, keeping awareness high while maintaining calm visual communication.",
      images: ["/assets/stock-12.png",
      "/assets/stock-6.png",
      ],
    },
  ],
},

    {
      id: "key-decisions",
      eyebrow: "Key Decisions & Rationale",
      sidebarTitle: "Key Decisions",
      sidebarSubtitle: "Why it was designed this way",
      status: "done",
      title: "Key decisions and rationale",
      layout: "rationale-list",
      items: [
        {
          label: "01 — Clarity over completeness",
          title: "Designing for beginners first",
          body:
            "Every layout decision was made with one constraint: the user has never used a stock platform before. Terminology is explained in context, data is chunked into digestible cards, and nothing assumes prior financial knowledge.",
        },
        {
          label: "02 — Synthetic data at scale",
          title: "26M data points, zero cost",
          body:
            "Real-time market APIs cost hundreds of dollars monthly. Instead of cutting scope, synthetic prices were generated using Geometric Brownian Motion, the same stochastic model used in quantitative finance, applied to real NASDAQ tickers from Alpha Vantage.",
        },
        {
          label: "03 — Color as the first language",
          title: "Understanding before reading",
          body:
            "Green, red, and amber signal portfolio state before the user processes any number. This reduces cognitive load especially for new investors who have not yet built intuition for raw figures.",
        },
        {
          label: "04 — Guided onboarding over open canvas",
          title: "Reducing first-session drop-off",
          body:
            "Rather than dropping users into an empty dashboard, the flow walks them through identity, portfolio, and insights step by step. Each stage has a clear goal, preventing the blank-slate paralysis that kills new-user retention.",
        },
        {
          label: "05 — AI scoped, not sprayed",
          title: "Smart API usage under constraints",
          body:
            "Gemini API calls are triggered at the component level rather than on every page load. This keeps the experience fast, stays within free-tier limits, and ensures AI insights appear only where they add real value.",
        },
      ],
    },

    {
      id: "delivered-and-learned",
      eyebrow: "Outcome",
      sidebarTitle: "Outcome",
      sidebarSubtitle: "Delivered and learned",
      status: "done",
      title: "What was delivered and what I learned",
      layout: "split-insights",
      columns: [
        {
          title: "What was delivered",
          items: [
            {
              label: "Full-stack web application",
              body:
                "Built end to end with React, Vite, Tailwind CSS, ShadCN, Node.js, Express, and PostgreSQL, from onboarding to AI-powered portfolio analysis.",
            },
            {
              label: "Synthetic market data engine",
              body:
                "A data generation pipeline using Geometric Brownian Motion across 13,000+ real NASDAQ tickers, producing around 26 million data points spanning January 2021 to May 2027 at zero cost.",
            },
            {
              label: "AI insights integration",
              body:
                "Gemini API integration that analyzes portfolio behavior and surfaces trend direction, volatility, and drawdown as plain-English insights instead of raw metrics.",
            },
            {
              label: "Portfolio management & watchlist",
              body:
                "Full portfolio CRUD with asset allocation visualization, historical performance across multiple time ranges, and a separate watchlist to track stocks without noise.",
            },
          ],
        },
        {
          title: "What I learned",
          items: [
            {
              tag: "Designing for the intimidated",
              body:
                "Building for financial beginners taught me that the barrier is not just complexity, it is the feeling of being locked out. Every design decision needs to actively signal that the user belongs here.",
            },
            {
              tag: "Engineering constraints as creative inputs",
              body:
                "The free-tier API limits and zero data budget did not shrink the project, they shaped it. Geometric Brownian Motion gave the system more scale and statistical realism than a paid feed would have for the same scope.",
            },
            {
              tag: "AI as interpretation, not decoration",
              body:
                "Integrating Gemini taught me how to present AI output so it feels trustworthy and actionable rather than overwhelming. The key was pairing every metric with a plain-language explanation of what it means for the user’s portfolio.",
            },
          ],
        },
      ],
    },
  ],
},
//medshift360
{
  id: "medshift-360",
  title: "Medshift 360",
  description:
    "A full-stack web application that streamlines hospital bed availability, emergency ambulance booking, and patient health management.",
  image: "/assets/card-img-ms.png",
  screen: "/assets/card-img-ms.png",
  tags: ["Group Project", "Healthcare", "Web"],
  techstack: ["React", "TypeScript", "ShadCN", "Tailwind CSS"],
  type: "project",
  link: "/project/medshift-360",
  status: "shipped",
  accent: "#2196F3",
  hero: "/assets/med-ss-bg.png",
  intro:
    "A full-stack healthcare web application built to make hospital access, emergency booking, and patient health management faster, clearer, and easier to use in real-world medical situations.",
  meta: {
    role: "Frontend Developer",
    team: "Group Project",
    timeline: "13 May 2025 - 21 Mar 2026",
    skills: ["Frontend", "UI Design", "Development", "Web"],
  },
  sections: [
    {
      id: "quick-summary",
      eyebrow: "Quick Summary",
      sidebarTitle: "Quick Summary",
      sidebarSubtitle: "Overview",
      status: "done",
      title: "A healthcare platform designed around urgency, clarity, and patient trust",
      subtitle:
        "Built to help patients and caregivers act quickly during emergencies and navigate routine hospital access with less friction.",
      body:
        "Medshift 360 is a healthcare web app designed and built for patients navigating medical emergencies and routine hospital access. The platform allows users to view real-time hospital bed availability, book emergency ambulances, manage their health profile, track insurance details, and receive AI-powered health insights, all from a mobile-first interface. Designed in Figma and built with React, TypeScript, Tailwind CSS, and ShadCN, the app prioritizes speed of access and clarity of information in high-stress situations. Every screen was designed around one question: what does a patient or caregiver need right now?",
      layout: "text",
    },
    {
      id: "flows",
      eyebrow: "Flows",
      sidebarTitle: "Flows",
      sidebarSubtitle: "Core product flows",
      status: "done",
      title: "The product was designed as a set of guided, high-priority healthcare flows",
      body:
        "Each screen was shaped to support users under pressure, reduce confusion, and help them reach action faster without losing control.",
      layout: "stacked-media-list",
      items: [
        {
          title: "Authentication Flow",
          subtitle:
            "Designed a multi-option login screen supporting password, Aadhaar, and OTP-based login, reducing barriers for users across different digital literacy levels. The sign-up form collects only essential fields upfront such as name, contact, Aadhaar, age, and gender, keeping onboarding short and friction-free.",
          images: [
            "/assets/med-shift-1.png",
            "/assets/med-shift-2.png",
            "/assets/med-shift-3.png",
          ],
        },
        {
          title: "Home Dashboard",
          subtitle:
            "The home screen surfaces the most critical information first, AI health insights, recent hospital activity, nearby hospitals, and suggested doctors, using progressive disclosure to avoid overwhelming the user. A prominent Book Emergency Ambulance CTA is placed at the bottom of the home screen, ensuring it stays reachable in one scroll.",
          images: [
            "/assets/med-shift-ss.png",
            "/assets/med-shift-4.png",
            "/assets/med-shift-5.png",
          ],
        },
        {
          title: "Emergency Booking Flow",
          subtitle:
            "Designed as a guided step-by-step process: trigger booking, review patient details, confirm emergency contacts, select hospital through AI auto or manual choice, and confirm. The flow reduces decision-making friction by showing only what is needed at each step, with a red confirm CTA to signal urgency and action.",
          images: [
            "/assets/med-shift-8.png",
            "/assets/med-shift-9.png",
            "/assets/med-shift-10.png",
            "/assets/med-shift-11.png",
            "/assets/med-shift-12.png",
            "/assets/med-shift-13.png",
          ],
        },
        {
          title: "Hospital Finder",
          subtitle:
            "The hospital finder combines a live map view with distance-based filtering and government/private toggles, giving users control without cognitive overload. Each hospital card surfaces the key metrics a patient needs immediately: distance, bed count, doctor count, and average arrival time.",
          images: [
            "/assets/med-shift-11.png",
            "/assets/med-shift-13.png",
          ],
        },
        {
          title: "Patient Details",
          subtitle:
            "A structured health profile screen stores conditions, medications, allergies, blood type, and emergency contacts in a single scannable layout, so critical information is never buried when it is needed most. The profile is designed to be filled incrementally, letting users start with basics and add medical history over time.",
          images: [
            "/assets/med-shift-12.png",
            "/assets/med-shift-14.png",
            "/assets/med-shift-15.png",
            "/assets/med-shift-17.png",
          ],
        },
        {
          title: "Insurance Management",
          subtitle:
            "The insurance screen centralizes policy details, provider information, coverage limits, and claim history, replacing the need to carry physical documents or switch between apps. Insurance data is surfaced contextually during hospital booking and emergency flows, so users never have to retrieve policy numbers under pressure.",
          images: [
            "/assets/med-shift-16.png"
          ],
        },
        {
          title: "AI Health Assistant",
          subtitle:
            "The AI assistant analyzes the patient’s stored profile and surfaces personalized health scores, lifestyle recommendations, medication reminders, and nearby hospital alerts based on the user’s actual data. Responses are designed to feel advisory rather than clinical, using plain-language explanations and clear next actions instead of anxiety-inducing diagnostic output.",
          images: [
            "/assets//med-shift-6.png"
          ],
        },
        {
          title: "Pharmacy Feature",
          subtitle:
            "The pharmacy screen lets users search for medications, check nearby availability, and upload prescriptions, reducing the gap between diagnosis and access to treatment. Medications linked to the patient’s active prescriptions are surfaced automatically, with reorder reminders built in to help users stay on course.",
          images: [
            "/assets/med-shift-18.png"
          ],
        },
      ],
    },
    {
      id: "key-decisions",
      eyebrow: "Key Decisions & Rationale",
      sidebarTitle: "Key Decisions",
      sidebarSubtitle: "Why it was designed this way",
      status: "done",
      title: "Key decisions and rationale",
      layout: "rationale-list",
      items: [
        {
          label: "01 — Emergency-first hierarchy",
          title: "Designing for urgency",
          body:
            "Every layout decision was made with one constraint in mind: in a medical emergency, every second counts. CTAs are large, flows are short, and critical information is always above the fold.",
        },
        {
          label: "02 — Progressive disclosure",
          title: "Surfacing only what is needed",
          body:
            "The dashboard shows high-priority content first, AI insights, nearby hospitals, and recent activity, while secondary features sit behind bottom navigation. This reduces cognitive load in high-pressure moments.",
        },
        {
          label: "03 — Multi-modal authentication",
          title: "Accessible login for all users",
          body:
            "Supporting password, Aadhaar, and OTP login ensures the app is accessible to users with varying levels of digital literacy, which is critical for a healthcare product meant for a broad user base.",
        },
        {
          label: "04 — AI-assisted decision making",
          title: "Reducing decision paralysis",
          body:
            "The AI auto-booking option in the emergency flow removes the burden of choosing a hospital under stress. Users can still override with manual selection, balancing automation with control.",
        },
        {
          label: "05 — Mobile-first, high-contrast design",
          title: "Clarity on small screens",
          body:
            "The app was designed mobile-first with high-contrast typography, generous touch targets, and a clean layout to ensure readability and usability in real-world conditions, including stress and poor lighting.",
        },
      ],
    },

    {
      id: "delivered-and-learned",
      eyebrow: "Outcome",
      sidebarTitle: "Outcome",
      sidebarSubtitle: "Delivered and learned",
      status: "done",
      title: "What was delivered and what I learned",
      layout: "split-insights",
      columns: [
        {
          title: "What was delivered",
          items: [
            {
              label: "Full-stack web application",
              body:
                "Built end to end with React, TypeScript, Tailwind CSS, and ShadCN, from authentication to emergency booking and hospital discovery.",
            },
            {
              label: "Emergency ambulance booking flow",
              body:
                "A guided multi-step booking experience with AI auto-selection and manual override, designed to minimize friction in critical moments.",
            },
            {
              label: "Patient health profile & insurance management",
              body:
                "Comprehensive profile screens covering health conditions, medications, allergies, emergency contacts, and insurance details in a clean, scannable layout.",
            },
            {
              label: "AI Health Assistant integration",
              body:
                "Personalized health score, lifestyle tips, nearby hospital alerts, and doctor suggestions surfaced contextually based on the patient’s profile.",
            },
          ],
        },
        {
          title: "What I learned",
          items: [
            {
              tag: "Designing for high-stakes moments",
              body:
                "Building a product used during emergencies taught me that clarity and speed of access matter more than visual richness. Every extra tap has a real cost.",
            },
            {
              tag: "UX and dev working together",
              body:
                "Designing and building the same product made me more intentional about component structure, interaction states, and how design decisions translate directly into code complexity.",
            },
            {
              tag: "AI as a UX layer",
              body:
                "Integrating AI insights into the interface taught me how to present AI output in a way that feels helpful and trustworthy rather than overwhelming or opaque.",
            },
          ],
        },
      ],
    },
  ],
},
//book buddy
{
    id: "book-buddy",
    title: "Book Buddy",
    description: "Discover, rate, and explore books like Letterboxd but for readers.",
    image: "/assets/book_ss_1.png",
    screen: "/assets/card-img-bb.png",
    tags: ["MVP", "Books", "iOS"],
    techstack: ["Swift", "SwiftUI", "Firebase", "Google Books API"],
    type: "project",
    link: "/project/book-buddy",
    status: "shipped",
    accent: "#4b141b",
    hero: "/assets/bookbuddy_bg.png",
    intro:
      "A book discovery MVP designed to make browsing, rating, and exploring books feel as engaging and serendipitous as discovering films on Letterboxd.",
    meta: {
      role: "iOS Developer",
      team: "Solo Project",
      timeline: "28 Jun 2025 - 02 Jul 2025",
      skills: ["iOS", "SwiftUI", "Product Design", "MVP"],
    },
    sections: [
      {
        id: "quick-summary",
        eyebrow: "Quick Summary",
        sidebarTitle: "Quick Summary",
        sidebarSubtitle: "Overview",
        status: "done",
        title: "A reading discovery app built around surprise, curiosity, and exploration",
        subtitle:
          "Designed to make finding books feel less like searching a database and more like stumbling onto something worth reading.",
        body:
          "Book Buddy is a minimum viable product built to make book discovery as engaging as movie discovery on Letterboxd. The app presents users with a curated set of 30 random books, customizable to 10 or 20, every time the main screen is refreshed, ensuring each visit feels fresh. Clicking a book cover reveals its details and suggests similar titles, encouraging deeper exploration. Users can rate their reading experience and search across the app’s database to find specific titles. Powered by the Google Books API and Firebase, Book Buddy blends randomness with control and helps users discover hidden literary gems they might never have searched for directly.",
        layout: "text",
      },
      {
        id: "flows",
        eyebrow: "Flows",
        sidebarTitle: "Flows",
        sidebarSubtitle: "Core product flows",
        status: "done",
        title: "The MVP was designed to make every refresh feel like a new shelf",
        body:
          "Each flow supports lightweight discovery, quick emotional reaction, and deeper exploration without asking too much from the user at once.",
        layout: "stacked-media-list",
        items: [
          {
            title: "Random Book Feed",
            subtitle:
              "The home feed refreshes with a randomized set of books so every visit feels fresh, creating the same kind of casual discovery loop that makes movie browsing addictive.",
            image: "/assets/book_ss_1.png",
          },
          {
            title: "Book Detail & Similar Titles",
            subtitle:
              "Clicking a book cover opens its details and reveals similar recommendations, turning a single moment of curiosity into a path for deeper browsing.",
            image: "/assets/book-ss-7.png",
          },
          {
            title: "Rating Experience",
            subtitle:
              "Users can rate their reading experience quickly, making the app feel personal while laying the groundwork for future recommendation logic.",
            image: "/assets/book-ss-5.png",
          },
        ],
      },
      {
        id: "key-decisions",
        eyebrow: "Key Decisions & Rationale",
        sidebarTitle: "Key Decisions",
        sidebarSubtitle: "Why it was designed this way",
        status: "done",
        title: "Key decisions and rationale",
        layout: "rationale-list",
        items: [
          {
            label: "01 — Discovery first",
            title: "Browsing had to feel fun before it felt efficient",
            body:
              "The random feed was central to the product because it creates repeat curiosity and encourages users to encounter books they would never think to search for directly.",
          },
          {
            label: "02 — Cover art as interface",
            title: "Visual pull drives engagement",
            body:
              "The UI was built around large cover imagery so the first interaction felt emotional and inviting rather than database-heavy.",
          },
          {
            label: "03 — Randomness with control",
            title: "Users needed both serendipity and direction",
            body:
              "Search and detail views balanced the randomness of the home feed, making the experience feel playful without losing practical usefulness.",
          },
          {
            label: "04 — API-conscious design",
            title: "Performance shaped the product architecture",
            body:
              "Caching recently fetched books and using lazy image loading helped keep the app responsive while limiting excessive API usage.",
          },
          {
            label: "05 — Ratings as future infrastructure",
            title: "Small interaction, bigger long-term value",
            body:
              "The rating system was lightweight in the MVP, but intentionally structured to support future preference modeling and recommendation logic.",
          },
        ],
      },
      {
        id: "delivered-and-learned",
        eyebrow: "Outcome",
        sidebarTitle: "Outcome",
        sidebarSubtitle: "Delivered and learned",
        status: "done",
        title: "What was delivered and what I learned",
        layout: "split-insights",
        columns: [
          {
            title: "What was delivered",
            items: [
              {
                label: "iOS book discovery MVP",
                body:
                  "Built in SwiftUI with a browsing experience centered on random discovery and visual exploration.",
              },
              {
                label: "Google Books API integration",
                body:
                  "Fetched and structured book data for feed browsing, detail views, and recommendations.",
              },
              {
                label: "Firebase-backed user interactions",
                body:
                  "Stored ratings and repeated-access data with Firebase and Firestore.",
              },
              {
                label: "Search and recommendation flows",
                body:
                  "Balanced serendipitous browsing with direct search and similar-title exploration.",
              },
            ],
          },
          {
            title: "What I learned",
            items: [
              {
                tag: "Discovery design",
                body:
                  "This project taught me how much product value can come from shaping mood and curiosity, not just functionality.",
              },
              {
                tag: "Visual systems matter",
                body:
                  "When content like book covers is central, interface design becomes about framing and supporting that content rather than competing with it.",
              },
              {
                tag: "Performance under API limits",
                body:
                  "Working with external APIs reinforced the need to treat caching and loading strategy as part of the user experience, not just backend optimization.",
              },
            ],
          },
        ],
      },
    ],
},
//GAME MANAGEMENT UI
{
    id: "game-management-ui",
    title: "Game Management UI",
    description:
      "An Xbox-inspired interface for tracking games, levels, and player stats.",
    image: "/assets/card-img-gme.png",
    tags: ["UI Sprint", "Gaming", "Concept"],
    techstack: ["Figma", "Adobe Animate"],
    type: "project",
    link: "/project/game-management-ui",
    status: "shipped",
    accent: "#16A34A",
    hero: "/assets/game_ss_bg.png",
    intro:
      "A one-day UI sprint exploring how gamers could manage their library, progress, and activity in a premium dashboard experience.",
    meta: {
      role: "UI/UX Designer",
      team: "Solo Project",
      timeline: "28 May 2024 - 25 May 2024",
      skills: ["UI Design", "Interaction Design", "Gaming UI", "Concept Work"],
    },
    sections: [
      {
        id: "quick-summary",
        eyebrow: "Quick Summary",
        sidebarTitle: "Quick Summary",
        sidebarSubtitle: "Overview",
        status: "done",
        title: "A gaming dashboard designed to feel premium, familiar, and highly visual",
        subtitle:
          "Built as a fast design sprint to explore how players could manage games, progress, and social activity from one place.",
        body:
          "Game Management UI is a one-day design sprint exploring how gamers could manage their library, track their progress, and engage with friends in a clean, modern interface. Inspired by a concept spotted on Pinterest and borrowing cues from the Xbox app’s layout, the design centralizes everything in a single dashboard, from owned games and level progress to recent activity and new discovery. The goal was to create an experience that feels both premium and instantly familiar to gamers. The concept also incorporates micro-animations such as hover effects and animated progress bars to add polish and motion to the static UI.",
        layout: "text",
      },
      {
        id: "flows",
        eyebrow: "Flows",
        sidebarTitle: "Flows",
        sidebarSubtitle: "Core product flows",
        status: "done",
        title: "The interface was shaped around visibility, progress, and social energy",
        body:
          "Each area of the design aimed to make a player feel informed about their own progress while staying connected to the larger gaming ecosystem.",
        layout: "stacked-media-list",
        items: [
          {
            title: "Game Library Overview",
            subtitle:
              "The library view was designed to make owned games feel collectible and easy to scan, with strong visual emphasis on cover art and quick access to key actions.",
            image: "/assets/game-ss-5.gif",
          },
          {
            title: "Progress Tracking",
            subtitle:
              "Player level and in-game progress were treated as core dashboard elements, with animated indicators helping progress feel more rewarding and visible.",
            image: "/assets/game-ss-4.png",
          },
          {
            title: "Discovery Layer",
            subtitle:
              "New titles and recommendations were integrated into the interface to keep the product feeling active and future-facing rather than purely archival.",
            image: "/assets/game-ss-3.png",
          },
        ],
      },
      {
        id: "key-decisions",
        eyebrow: "Key Decisions & Rationale",
        sidebarTitle: "Key Decisions",
        sidebarSubtitle: "Why it was designed this way",
        status: "done",
        title: "Key decisions and rationale",
        layout: "rationale-list",
        items: [
          {
            label: "01 — Familiarity first",
            title: "Inspired by interfaces players already understand",
            body:
              "Borrowing structural cues from Xbox made the experience feel approachable immediately, which is important in a dashboard with multiple information layers.",
          },
          {
            label: "02 — Visual dominance",
            title: "Games needed to feel like the main character",
            body:
              "Cover art, progress bars, and bold panels were used to ensure the dashboard felt unmistakably about games, not generic account management.",
          },
          {
            label: "03 — Motion as polish",
            title: "Micro-interactions add perceived quality",
            body:
              "Small animations such as hover states and progress movement were used to make the interface feel responsive and premium without turning it into spectacle.",
          },
          {
            label: "04 — One-surface utility",
            title: "Everything important in one dashboard",
            body:
              "The sprint focused on centralization, reducing the need for deep navigation by making key stats, actions, and social signals visible together.",
          },
          {
            label: "05 — Fast execution with strong hierarchy",
            title: "A one-day sprint still needs structure",
            body:
              "The compressed timeline made hierarchy even more important, since a clear information framework was necessary to support quick design decisions.",
          },
        ],
      },
      {
        id: "delivered-and-learned",
        eyebrow: "Outcome",
        sidebarTitle: "Outcome",
        sidebarSubtitle: "Delivered and learned",
        status: "done",
        title: "What was delivered and what I learned",
        layout: "split-insights",
        columns: [
          {
            title: "What was delivered",
            items: [
              {
                label: "Gaming dashboard concept",
                body:
                  "Created a compact Xbox-inspired management UI for tracking games, progress, and social activity.",
              },
              {
                label: "Figma UI system",
                body:
                  "Designed a cohesive visual framework for cards, navigation, progress displays, and media-led sections.",
              },
              {
                label: "Micro-animation prototypes",
                body:
                  "Used Adobe Animate to bring subtle life to progress bars, hover states, and selection interactions.",
              },
              {
                label: "One-day sprint output",
                body:
                  "Completed a polished concept in a compressed timeframe as a speed and creativity challenge.",
              },
            ],
          },
          {
            title: "What I learned",
            items: [
              {
                tag: "Designing under time pressure",
                body:
                  "A short sprint taught me how much stronger decisions become when the hierarchy is clear and the visual direction is locked early.",
              },
              {
                tag: "Interaction polish matters",
                body:
                  "Even subtle motion can change how premium and satisfying a gaming interface feels, especially in progress-heavy dashboards.",
              },
              {
                tag: "Genre-sensitive design",
                body:
                  "Game interfaces need a different emotional temperature than productivity apps. They should still be functional, but they also need energy and character.",
              },
            ],
          },
        ],
      },
    ],
},
//SPOTIFY VISION PRO
{
    id: "spotify-vision-pro",
    title: "Spotify for Vision Pro",
    description:
      "A futuristic reimagining of Spotify for Apple’s spatial computing platform.",
    image: "/assets/card-img-spt.png",
    tags: ["Concept", "Spatial UI", "Figma"],
    techstack: ["Figma", "Apple Design Resources"],
    type: "project",
    link: "/project/spotify-vision-pro",
    status: "shipped",
    accent: "#1DB954",
    hero: "/assets/spotify_img_bg.png",
    intro:
      "A conceptual redesign of Spotify for Apple Vision Pro, exploring how music browsing and playback could evolve in a spatial interface.",
    meta: {
      role: "UI/UX Designer",
      team: "Solo Concept",
      timeline: "23 Apr 2024 - 24 Apr 2024",
      skills: ["Spatial UI", "Figma", "Concept Design", "Prototyping"],
    },
    sections: [
      {
        id: "quick-summary",
        eyebrow: "Quick Summary",
        sidebarTitle: "Quick Summary",
        sidebarSubtitle: "Overview",
        status: "done",
        title: "A Spotify concept shaped for depth, presence, and spatial comfort",
        subtitle:
          "Built to imagine how music interaction could feel inside Apple’s mixed-reality environment.",
        body:
          "This project is a conceptual UI redesign of Spotify adapted for Apple Vision Pro’s immersive spatial environment. Created in Figma, the design explores how music discovery and playback might evolve in a mixed-reality interface. Inspired by a Pinterest idea and grounded in Apple’s official design resources, the concept uses depth, layering, and subtle animation to create a sense of space and presence. The player screen floats in a 3D-like environment, with album art, playlists, and controls arranged for comfortable eye and hand interaction. Browsing was reimagined as an interactive carousel of floating albums, blending Spotify’s brand with Apple’s spatial design language.",
        layout: "text",
      },
      {
        id: "flows",
        eyebrow: "Flows",
        sidebarTitle: "Flows",
        sidebarSubtitle: "Core product flows",
        status: "done",
        title: "The concept explored music as an inhabitable interface rather than a flat screen",
        body:
          "Each screen direction was designed around comfort, discoverability, and a stronger sense of presence than traditional music apps allow.",
        layout: "stacked-media-list",
        items: [
          {
            title: "Spatial Player Screen",
            subtitle:
              "The player was treated as a floating focal surface, bringing album art and playback controls into the foreground while pushing secondary information into softer background layers.",
            image: "/assets/song_ss_1.png",
          },
          {
            title: "Playlist Navigation Panels",
            subtitle:
              "Playlists were arranged in layered side panels to reduce clutter and make selection feel more natural for eye and hand interaction.",
            image: "/assets/song_ss_2.png",
          },
          {
            title: "Lyrics karoke mode",
            subtitle:
              "Listen to the song and sing along with the lyrics in a mode that highlights the current line and creates a more engaging, interactive listening experience.",
            image: "/assets/song_ss_3.png",
          },
        ],
      },
      {
        id: "key-decisions",
        eyebrow: "Key Decisions & Rationale",
        sidebarTitle: "Key Decisions",
        sidebarSubtitle: "Why it was designed this way",
        status: "done",
        title: "Key decisions and rationale",
        layout: "rationale-list",
        items: [
          {
            label: "01 — Spatial comfort first",
            title: "Designed for prolonged viewing, not spectacle",
            body:
              "The interface used large typography, clear spacing, and restrained motion so users could focus comfortably without visual fatigue in a mixed-reality setting.",
          },
          {
            label: "02 — Depth as hierarchy",
            title: "Distance became an information tool",
            body:
              "Foreground, midground, and background placement helped organize priority more intuitively than flat panel stacking would allow.",
          },
          {
            label: "03 — Brand continuity",
            title: "Spotify still needed to feel like Spotify",
            body:
              "Even within Apple’s spatial design language, the concept retained recognizable music-first energy and visual familiarity rather than becoming a generic Vision Pro demo.",
          },
          {
            label: "04 — Minimal motion",
            title: "Animation supported clarity instead of stealing focus",
            body:
              "Subtle motion and scaling cues were used to suggest interaction and depth without turning the concept into a distraction-heavy environment.",
          },
          {
            label: "05 — Mixed-reality browsing",
            title: "Discovery needed to feel more physical",
            body:
              "The floating album carousel made browsing feel spatial and tactile while staying within interaction patterns users could still quickly understand.",
          },
        ],
      },
      {
        id: "delivered-and-learned",
        eyebrow: "Outcome",
        sidebarTitle: "Outcome",
        sidebarSubtitle: "Delivered and learned",
        status: "done",
        title: "What was delivered and what I learned",
        layout: "split-insights",
        columns: [
          {
            title: "What was delivered",
            items: [
              {
                label: "Vision Pro Spotify concept",
                body:
                  "Created a speculative mixed-reality redesign of Spotify centered on immersive playback and discovery.",
              },
              {
                label: "Spatial UI exploration",
                body:
                  "Applied Apple’s spatial design resources to interface layout, depth hierarchy, and interaction framing.",
              },
              {
                label: "Figma prototype screens",
                body:
                  "Designed player, browsing, and navigation states that suggest 3D interaction within a readable structure.",
              },
              {
                label: "Brand-adapted visual system",
                body:
                  "Balanced Spotify’s identity with Apple-inspired spatial interface patterns.",
              },
            ],
          },
          {
            title: "What I learned",
            items: [
              {
                tag: "Designing for presence",
                body:
                  "This concept pushed me to think about interface depth, distance, and comfort as core parts of hierarchy rather than purely visual effects.",
              },
              {
                tag: "Speculative design as systems thinking",
                body:
                  "Even a concept project benefits from strong rules. The more futuristic the surface looks, the more the internal logic has to hold together.",
              },
              {
                tag: "Brand translation across platforms",
                body:
                  "A strong product identity can survive platform shifts, but only if you preserve the emotional core while adapting the interaction model honestly.",
              },
            ],
          },
        ],
      },
    ],
},
//LETTER PEEK
{
    id: "letter-peek",
    title: "Letter Peek",
    description:
      "A sleek Chrome extension for real-time font inspection with Apple-inspired design.",
    image: "/assets/card-img-pek.png",
    tags: ["Extension", "Typography", "Tool"],
    techstack: ["HTML", "CSS", "JavaScript"],
    type: "project",
    link: "/project/letter-peek",
    status: "shipped",
    accent: "#111111",
    hero: "/assets/font-ss-bg.png",
    intro:
      "A Chrome extension built to make real-time font inspection feel fast, useful, and visually polished for designers and developers.",
    meta: {
      role: "Frontend Developer",
      team: "Solo Project",
      timeline: "2026",
      skills: ["JavaScript", "Extension UI", "Typography Tools", "Frontend"],
    },
    sections: [
      {
        id: "quick-summary",
        eyebrow: "Quick Summary",
        sidebarTitle: "Quick Summary",
        sidebarSubtitle: "Overview",
        status: "done",
        title: "A typography inspection tool designed to be as polished as it is practical",
        subtitle:
          "Built for designers and developers who want instant font details without sacrificing visual quality.",
        body:
          "Letter Peek is a custom-built Chrome extension that allows designers and developers to instantly view font properties of any text on a webpage. Once activated, hovering over a text element reveals a smooth glassmorphic overlay displaying details like font family, size, weight, line height, and color. Inspired by Apple’s WWDC 2026 visual language, the UI blends translucent backgrounds, soft shadows, and subtle blur effects for a modern, premium feel. The goal was to merge functionality with aesthetics, creating a tool that is useful for typography research and also enjoyable to use.",
        layout: "text",
      },
      {
        id: "my-role",
        eyebrow: "My Role",
        sidebarTitle: "My Role",
        sidebarSubtitle: "Tool design and build",
        status: "done",
        title: "I built both the inspection logic and the visual experience around it",
        body:
          "The project combined low-level DOM style extraction with a premium interface layer so the extension felt fast, lightweight, and purposefully designed.",
        layout: "options",
        options: [
          {
            title: "Typography Inspection Logic",
            body:
              "Built the core font data extraction using JavaScript’s getComputedStyle method to capture font family, size, weight, line height, and color dynamically from hovered text.",
            image: "/assets/font-ss-1.png",
          },
          {
            title: "Glassmorphic UI Design",
            body:
              "Designed the extension overlay with translucent layers, soft shadows, gradients, and blur effects inspired by Apple’s WWDC 2026 visual language.",
            image: "/assets/font-ss-2.png",
          },
          {
            title: "Interaction & Positioning",
            body:
              "Handled card positioning, real-time hover updates, popup controls, and performance optimization so the experience stayed responsive without blocking the page content.",
            image: "/assets/font-ss-3.png",
          },
        ],
      },
      {
        id: "flows",
        eyebrow: "Flows",
        sidebarTitle: "Flows",
        sidebarSubtitle: "Core product flows",
        status: "done",
        title: "The extension was designed to make inspection feel instant and unobtrusive",
        body:
          "Each interaction focused on reducing friction so the tool could disappear into the workflow while still feeling beautifully made.",
        layout: "stacked-media-list",
        items: [
          {
            title: "Hover-to-Inspect Flow",
            subtitle:
              "Once activated, the extension lets users inspect any text element just by hovering, turning typography auditing into a near-instant action rather than a multi-step devtools task.",
            image: "/assets/font-ss-1.png",
          },
          {
            title: "Floating Detail Card",
            subtitle:
              "The info card appears close to the hovered text while avoiding obstruction, making it easy to read font details without losing sight of the original element.",
            image: "/assets/font-ss-2.png",
          },
          {
            title: "Popup Control Layer",
            subtitle:
              "The popup interface provides simple on and off controls, keeping activation lightweight and easy to understand without turning the extension into a settings-heavy tool.",
            image: "/assets/font-ss-3.png",
          },
          {
            title: "Real-Time Typography Updates",
            subtitle:
              "Event listeners update the visible font properties in real time as the user moves across the page, supporting fast visual auditing and comparison workflows.",
            image: "/assets/font-ss-1.png",
          },
        ],
      },
      {
        id: "key-decisions",
        eyebrow: "Key Decisions & Rationale",
        sidebarTitle: "Key Decisions",
        sidebarSubtitle: "Why it was designed this way",
        status: "done",
        title: "Key decisions and rationale",
        layout: "rationale-list",
        items: [
          {
            label: "01 — Tooling without intimidation",
            title: "Inspection had to feel lighter than devtools",
            body:
              "The extension was designed to make typography research feel immediate and low-friction, especially for users who want answers quickly without opening full developer panels.",
          },
          {
            label: "02 — Aesthetic utility",
            title: "Usefulness and visual polish were treated equally",
            body:
              "The glassmorphic overlay was not just decorative. It was meant to make the tool feel premium and pleasant enough to use repeatedly.",
          },
          {
            label: "03 — Non-blocking positioning",
            title: "The card needed to inform without getting in the way",
            body:
              "Placement logic was shaped around proximity and visibility so the inspection card could stay helpful without hiding the very text being inspected.",
          },
          {
            label: "04 — Real-time responsiveness",
            title: "Hover interactions had to feel immediate",
            body:
              "Performance mattered because even slight lag would make the tool feel unreliable during rapid inspection across multiple text elements.",
          },
          {
            label: "05 — Lightweight control model",
            title: "The extension needed simple activation, not a complicated interface",
            body:
              "The popup was intentionally minimal so the product could stay focused on one core job instead of becoming a toolbox with unnecessary overhead.",
          },
        ],
      },
      {
        id: "delivered-and-learned",
        eyebrow: "Outcome",
        sidebarTitle: "Outcome",
        sidebarSubtitle: "Delivered and learned",
        status: "done",
        title: "What was delivered and what I learned",
        layout: "split-insights",
        columns: [
          {
            title: "What was delivered",
            items: [
              {
                label: "Chrome extension for font inspection",
                body:
                  "Built a lightweight browser tool for extracting and displaying typography details from live webpages.",
              },
              {
                label: "Real-time overlay UI",
                body:
                  "Created a floating inspection card that updates dynamically with font information during hover interactions.",
              },
              {
                label: "Apple-inspired glassmorphic styling",
                body:
                  "Designed a premium visual layer using blur, translucency, gradients, and soft shadows.",
              },
              {
                label: "Responsive interaction model",
                body:
                  "Implemented lightweight popup controls and optimized hover behavior for smooth performance.",
              },
            ],
          },
          {
            title: "What I learned",
            items: [
              {
                tag: "Tool UX is still UX",
                body:
                  "Even highly functional utilities benefit enormously from thoughtful interaction design. Small tools deserve the same care as larger products.",
              },
              {
                tag: "Visual polish changes perceived quality",
                body:
                  "The same feature set can feel dramatically better when the interface is calm, elegant, and clearly intentional.",
              },
              {
                tag: "Performance is trust",
                body:
                  "Because the tool depends on hover responsiveness, I learned again that speed is not an enhancement. It is part of the product’s credibility.",
              },
            ],
          },
        ],
      },
    ],
},
//blog
{
  id: "first-blog",
  title: "Not Everything Has To Be Perfect To Be Said",
  description: "Learning to hit publish without overthinking",
  image: "/assets/blog_img_3.png",
  tags: ["Blog", "Personal"],
  techstack: ["Writing"],
  type: "blogs", // 👈 new type (optional but clean)
  link: "/project/first-blog",
  status: "published",
  hero: "/assets/blog_img_1.png",

  intro:
    "A raw first post about overthinking, expression, and why starting messy matters more than waiting for perfect.",

  meta: {
    role: "Writer",
    team: "Solo",
    timeline: "2026",
    skills: ["Writing", "Storytelling"],
  },

  sections: [
    {
      id: "blog-main",
      eyebrow: "Blog",
      sidebarTitle: "Article",
      sidebarSubtitle: "Personal writing",
      status: "done",

      title: "I almost didn’t write this",
      subtitle: "On imperfection and starting anyway",

      layout: "blog",
      content: `
I almost didn't write this post. Why? Because I kept waiting for it to sound perfect - and then I realized… it never will. So here we are. Welcome to my first post (or blog? still not sure what to call it). I'll keep it real and not too formal, so you don't get bored and dip out.
Over the past couple of days, I've been surfing the internet on how to write a blog and trying to psych myself up to put my thoughts out there. But all those tabs and tips got me overwhelmed, to be real. Then, I understood that I will never be ready to express myself in the perfect sense -Hol'up, I didn't even introduce myself. I'm Ragav - a final-year Computer Science Engineering student. I vibe more with frontend development and UI design than backend, because I find frontend development to be more creative (no offense to backend folks).

Back to why I said I'll never express myself perfectly: the art of expression is always evolving. Every day, the things you see, hear, and learn change the way you put yourself out there. For example, let's say I design a website or a cover and I love it after finishing it. Then, a year later, after designing a few more projects, I'd probably end up hating it - because my vision and way of designing would have changed. And that's completely natural. Self-expression adapts and grows with each experience. So I think this intro would be like a disclaimer that the blog is not gonna be "perfect". For me, blogging is like practicing in public. Some posts won't land, some sentences won't shine - but that's the point. It's a way to grow, experiment, and get a little better each time I hit publish.
At first, I'll admit - part of me thought blogging would just look cool on my portfolio (go check it out if you're curious). But honestly, it's more than that. Writing here feels like a way of throwing them into the world, even if it's just a handful of people who stumble across it.

Not everything I write is going to be deep or even important. Sometimes it'll be random thoughts, sometimes it'll be me talking about design, college chaos, music that hits me at the right time, or even movies that make me think about life differently. And that's the fun part - this blog doesn't have to be perfect to be worth saying.

As I wrap this up, here's the funny part: I'm not someone who usually expresses much. 
But that got me thinking of a line from The Sopranos (yeah, I'm hooked on it right now):
"Whatever happened to Gary Cooper? The strong, silent type? - Tony Soprano"
Here, Tony was talking about how people don't stay quiet and tough anymore. But me? I don't want to be the strong, silent type. I'd rather speak up, put stuff out there, even if it's messy.
      `,

      outroImage: "/assets/blog_img_2.png"
    }
  ]
},

];

export const getProjectById = (id) =>
  PROJECTS.find((project) => project.id === id);
